import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/auth';
import { defaultFormState } from './constants';
import { getLoyaltyProgramsByCompany } from '../../services/loyalty/getLoyaltyProgramsByCompany';
import { createLoyaltyProgram } from '../../services/loyalty/createLoyaltyProgram';
import { updateLoyaltyProgram } from '../../services/loyalty/updateLoyaltyProgram';
import { deleteLoyaltyProgram } from '../../services/loyalty/deleteLoyaltyProgram';
import { parseProgramToForm } from './utils/parseProgramToForm';
import { parseFormToProgram } from './utils/parseFormToProgram';
import { validateLoyaltyForm } from './utils/validateLoyaltyForm';
import { toastSuccess, toastError } from '../../toasts';

export const useLoyaltyProgram = () => {
  const { id_empresa } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [form, setForm] = useState(defaultFormState);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [pendingActivation, setPendingActivation] = useState(null);

  const onChange = (label) => {
    return (value) => {
      setForm((state) => ({ ...state, [label]: value }));
    };
  };

  const loadPrograms = async () => {
    setLoading(true);
    const response = await getLoyaltyProgramsByCompany(id_empresa);
    setPrograms(response.success ? response.data : []);
    setLoading(false);
  };

  const openCreate = () => {
    setForm(defaultFormState);
    setOpenModal(true);
  };

  const openEdit = (programData) => {
    setForm(parseProgramToForm(programData));
    setOpenModal(true);
  };

  const closeModal = () => setOpenModal(false);

  const save = async () => {
    try {
      setSaving(true);

      const validatedForm = await validateLoyaltyForm(form);
      if (!validatedForm) return;

      const payload = parseFormToProgram(validatedForm, id_empresa);
      const response = payload.Id
        ? await updateLoyaltyProgram(payload)
        : await createLoyaltyProgram(payload);

      if (!response.success) {
        toastError('Não foi possível salvar o programa de fidelidade');
        return;
      }

      await loadPrograms();
      toastSuccess('Programa de fidelidade salvo com sucesso');
      setOpenModal(false);
    } catch (error) {
      console.log(error);
      toastError('Não foi possível salvar o programa de fidelidade');
    } finally {
      setSaving(false);
    }
  };

  const applyToggle = async (programData, resetProgress) => {
    const response = await updateLoyaltyProgram({
      Id: programData.Id,
      IdEmpresa: id_empresa,
      Status: !programData.Status,
      ZerarProgressoClientes: Boolean(resetProgress),
    });

    if (!response.success) {
      toastError('Não foi possível atualizar o status da fidelidade');
      return;
    }

    await loadPrograms();
    toastSuccess(response.data.Status ? 'Fidelidade ativada' : 'Fidelidade desativada');
  };

  const toggleStatus = (programData) => {
    if (!programData.Status) {
      const currentlyActive = programs.find((p) => p.Status && p.Id !== programData.Id);
      if (currentlyActive) {
        setPendingActivation(programData);
        return;
      }
    }

    return applyToggle(programData);
  };

  const confirmActivation = async (resetProgress) => {
    const target = pendingActivation;
    setPendingActivation(null);
    if (target) await applyToggle(target, resetProgress);
  };

  const cancelActivation = () => setPendingActivation(null);

  const askDelete = (programData) => setDeleting(programData);

  const cancelDelete = () => setDeleting(null);

  const confirmDelete = async () => {
    const response = await deleteLoyaltyProgram(deleting.Id);

    if (!response.success) {
      toastError('Não foi possível excluir o programa de fidelidade');
      return;
    }

    setPrograms((prev) => prev.filter((p) => p.Id !== deleting.Id));
    setDeleting(null);
    toastSuccess('Programa de fidelidade excluído');
  };

  useEffect(() => {
    if (id_empresa) loadPrograms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id_empresa]);

  return {
    programs,
    form,
    loading,
    saving,
    openModal,
    deleting,
    pendingActivation,
    onChange,
    openCreate,
    openEdit,
    closeModal,
    save,
    toggleStatus,
    confirmActivation,
    cancelActivation,
    askDelete,
    cancelDelete,
    confirmDelete,
  };
};
